import {InstanceContext} from "@/types/InstanceContext"
import {handOver, takeHandover} from "@/utils/handover"

/**
 * A leitura de uma coleção inteira, que a listagem e o quadro fazem IGUAL — e a entrega de uma
 * para a outra.
 *
 * São duas visões da mesma coisa, e por muito tempo cada uma montou a própria requisição. As
 * duas pediam quase o mesmo: a listagem sem os três carimbos de tempo, o quadro com eles — e o
 * quadro os usa só para o prazo das raias terminais, que a listagem ignora. Sendo o superconjunto
 * inofensivo para ela, a diferença não tinha razão de existir, e enquanto existiu o que uma leu
 * não servia para a outra.
 *
 * Unificadas, alternar Lista ↔ Kanban deixa de ir ao servidor: o que a tela que sai já tem é
 * exatamente o que a que entra leria.
 */

/**
 * `rels`, o identificador, o corpo, a situação — e os carimbos, que só o prazo das raias usa. E as
 * falhas de cada item (`failures`), que só a listagem marca: pedidas pelas duas pelo mesmo motivo
 * dos carimbos — a leitura que uma entrega à outra tem de servir às duas. Custam pouco: contas sobre
 * o que a leitura já calculou, e uma leitura leve por campo único.
 *
 * E a autoria (`createdBy`, `updatedBy`), que o markdown do card mostra pelo `{{_createdBy}}` (ver
 * `markdownMentions`) — só a ficha a pedia, e no card o campo saía vazio.
 */
const META = ["rels", "identifier", "instance", "state", "stateChangedAt", "updatedAt", "createdAt", "createdBy", "updatedBy", "failures"]

/**
 * `rel-domains`: a lista de candidatos de cada coleção apontada.
 *
 * Uma consulta por coleção apontada, e a parte cara da leitura — mas de carona ela não custa ida
 * e volta nenhuma. Serve a três coisas: as colunas e os filtros de relacionamento da listagem,
 * as raias de um valor sem item nenhum no quadro, e os campos de relacionamento do formulário de
 * criar, que por isso abre sem requisição.
 */
export function collectionReadConfig(candidates: boolean, filter?: any) {
    return {
        meta: META,
        ...(candidates ? {options: ["rel-domains"]} : {}),
        ...(filter ? {filter} : {})
    }
}

/**
 * A chave da entrega: a COLEÇÃO, e não a visão.
 *
 * É o que faz a entrega valer nos dois sentidos com uma chave só — quem sai não precisa saber
 * para qual das duas visões está entregando, e quem chega não precisa saber de qual veio.
 */
export function collectionReadKey(context: InstanceContext): string {
    return `collection:${context.system}/${context.idea}`
}

/**
 * Deixa a leitura para a visão irmã, se for para lá que se está indo.
 *
 * **Só com os candidatos.** Entregar sem eles diria "esta coleção não tem candidato nenhum", e a
 * visão que chega aceitaria a mentira em vez de ir buscar — a mesma armadilha que o
 * `knownForCreate` evita devolvendo `null` em vez de um mapa vazio.
 *
 * **Só para a irmã**, e por isso quem chama diz o nome da rota de destino: no `beforeUnmount` o
 * `$route` já é o da tela que entra, então dá para perguntar. Sem essa condição, sair de uma
 * coleção para qualquer outro lugar deixaria uma entrega pendente esperando uma viagem que
 * ninguém fez.
 *
 * **E só quando se está indo A ALGUM LUGAR**, que é o `from`: a visão também se desmonta quando o
 * hospedeiro a remonta de propósito para RECARREGAR — o quadro faz isso depois de excluir ou
 * editar um item. Ali o destino é a própria rota de origem, a condição acima dava "irmã", e a
 * tela entregava a leitura velha **para si mesma**: montava de novo, encontrava a entrega,
 * aceitava-a em vez de ir ao servidor, e o item excluído continuava na tela. Recarregar que não
 * relê não é recarregar.
 */
export function handOverCollection(
    context: InstanceContext, routeName: string | null | undefined, read: any,
    from: string | null | undefined
): void {
    const sibling = routeName === 'list-instances' || routeName === 'kanban'
    const travelling = routeName !== from
    if (!sibling || !travelling || !read || !read.relDomains) return
    handOver(collectionReadKey(context), read)
}

/** O que a visão irmã deixou para esta coleção, ou null — e então se lê, como sempre. */
export function takeCollectionRead(context: InstanceContext): any | null {
    return takeHandover(collectionReadKey(context))
}
